/**
 * Para onde leva uma notificação — funções puras.
 *
 * O servidor diz o destino em `data.target` (ex.: `/dashboard/calendar?evento=<id>`,
 * ou `?dia=<YYYY-MM-DD>` no resumo da manhã). Só se segue um caminho DA APP:
 * começa por uma barra e não por duas (`//outro-site.com` seria um redirecionamento
 * para fora). Sem `target`, as de calendário caem no evento ou no dia.
 */

import { calendarEventPath, ROUTES } from "@/routes/paths";

interface NotificationLike {
  reference_type?: string | null;
  reference_id?: string | null;
  data?: Record<string, unknown> | null;
}

/** Um destino interno seguro, ou `null`. */
export function safeInternalPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return null;
  return value;
}

export function notificationTarget(n: NotificationLike): string | null {
  const fromServer = safeInternalPath(n.data?.target);
  if (fromServer) return fromServer;

  if (n.reference_type === "calendar_event" && n.reference_id) return calendarEventPath(n.reference_id);
  if (n.reference_type === "calendar_daily_summary") {
    const dia = typeof n.data?.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(n.data.date) ? n.data.date : n.reference_id;
    return dia && /^\d{4}-\d{2}-\d{2}$/.test(dia) ? `${ROUTES.CALENDAR}?dia=${dia}` : ROUTES.CALENDAR;
  }
  return null;
}

/** O tipo de notificação de calendário (`data.kind`), se for uma. */
export type CalendarNotificationKind = "invite" | "changed" | "cancelled" | "rsvp" | "removed" | "reminder" | "invite_reminder";

const KINDS: CalendarNotificationKind[] = ["invite", "changed", "cancelled", "rsvp", "removed", "reminder", "invite_reminder"];

export function calendarKind(n: NotificationLike): CalendarNotificationKind | null {
  const kind = n.data?.kind;
  return typeof kind === "string" && (KINDS as string[]).includes(kind) ? (kind as CalendarNotificationKind) : null;
}

/** Estas mudam a contagem de convites por responder (chega um convite, cai um, alguém responde…). */
export const AFFECTS_PENDING_INVITES: CalendarNotificationKind[] = ["invite", "invite_reminder", "changed", "cancelled", "removed", "rsvp"];

export const isCalendarNotification = (n: NotificationLike): boolean => n.reference_type === "calendar_event" || n.reference_type === "calendar_daily_summary";

/**
 * Para onde leva uma notificação — o destino do servidor primeiro, depois os caminhos das
 * notificações que já existiam antes dos alertas (formação, férias, baixas).
 * Uma só função para o sino e para o centro de notificações abrirem o mesmo sítio.
 */
export function notificationPath(n: NotificationLike & { notification_type?: string }): string | null {
  const target = notificationTarget(n);
  if (target) return target;
  if (n.reference_type === "training" && n.reference_id) return `/dashboard/hr/trainings/${n.reference_id}`;
  if (n.reference_type === "kb_article" && n.reference_id) return `/dashboard/hr/knowledge-base/${n.reference_id}`;
  if (n.reference_type === "vacation") return "/dashboard/hr/vacation-requests";
  if (n.notification_type === "baixa_punch") {
    const recordDate = typeof n.data?.record_date === "string" ? n.data.record_date : null;
    return recordDate ? `/dashboard/hr/time-tracking?date=${recordDate}` : "/dashboard/hr/time-tracking";
  }
  if (n.reference_type === "sick_leave") return "/dashboard/hr/sick-leave";
  // Convite para um processo: abre-o logo no editor.
  if (n.reference_type === "process_map" && n.reference_id) return `/dashboard/processes/${n.reference_id}`;
  return null;
}
